/** Filter for global admin lists/metrics tied to demo broker orgs (not demo PEO Vendors). */
export type DemoEnvironmentFilter = 'exclude' | 'include' | 'only';

export const DEFAULT_DEMO_ENVIRONMENT_FILTER: DemoEnvironmentFilter = 'exclude';

/** PostgREST query builder — generic to avoid Supabase generic depth limits. */
export function applyApplicationDemoFilter<T>(
  query: T,
  filter: DemoEnvironmentFilter = DEFAULT_DEMO_ENVIRONMENT_FILTER
): T {
  const q = query as { eq: (column: string, value: boolean) => T };
  if (filter === 'exclude') {
    return q.eq('excluded_from_admin_metrics', false);
  }
  if (filter === 'only') {
    return q.eq('excluded_from_admin_metrics', true);
  }
  return query;
}

/** PostgREST query builder — generic to avoid Supabase generic depth limits. */
export function applyProviderDemoFilter<T>(
  query: T,
  filter: DemoEnvironmentFilter = DEFAULT_DEMO_ENVIRONMENT_FILTER
): T {
  const q = query as { eq: (column: string, value: boolean) => T };
  if (filter === 'exclude') {
    return q.eq('is_demo_environment', false);
  }
  if (filter === 'only') {
    return q.eq('is_demo_environment', true);
  }
  return query;
}

export function isProductionDemoFilter(filter: DemoEnvironmentFilter): boolean {
  return filter === 'exclude';
}

/** @deprecated Use is_demo_environment flag instead. Kept for transitional reads only. */
export function isLegacyDemoProviderName(name: string | null | undefined): boolean {
  const n = (name ?? '').trim().toLowerCase();
  return n === 'peo provider' || n.includes('demo') || n.includes('test');
}

export function isDemoPeoProvider(
  provider: { is_demo_environment?: boolean | null; name?: string | null } | null | undefined
): boolean {
  if (provider?.is_demo_environment === true) return true;
  if (provider?.is_demo_environment === false) return false;
  return isLegacyDemoProviderName(provider?.name);
}

export type AdminClientRevenueStats = {
  totalClients: number;
  totalEmployees: number;
  totalEstimatedRevenue: number;
  revenuePerEmployee: number;
  avgEmployeesPerClient: number;
  clientsPerMillion: number;
  realAssignedCount: number;
  winRate: number;
};

type ClientRevenueRow = {
  application_id: string;
  provider: { id?: string; commission_percentage?: number | null; is_demo_environment?: boolean | null } | null;
  accepted_quote: { id?: string; annual_admin_cost?: number | null } | null;
};

type AssignmentRevenueRow = {
  application_id: string;
  peo_providers: { id?: string; name?: string; is_demo_environment?: boolean | null } | null;
};

export function computeAdminClientRevenueStats(
  clients: ClientRevenueRow[] | null | undefined,
  assignedApplications: AssignmentRevenueRow[] | null | undefined,
  employeeCounts: Record<string, number>,
  options?: { excludeDemoProvidersInOrgScope?: boolean }
): AdminClientRevenueStats {
  const excludeDemoProviders = options?.excludeDemoProvidersInOrgScope ?? true;

  const productionClients = (clients ?? []).filter((client) => {
    if (!excludeDemoProviders) return true;
    return client.provider?.is_demo_environment !== true;
  });

  const realAssignedApplications = (assignedApplications ?? []).filter((assignment) => {
    if (!excludeDemoProviders) return true;
    const provider = assignment.peo_providers;
    if (provider?.is_demo_environment === true) return false;
    if (
      (provider?.is_demo_environment === null || provider?.is_demo_environment === undefined) &&
      isLegacyDemoProviderName(provider?.name)
    )
      return false;
    return true;
  });

  const uniqueRealAssignedAppIds = new Set(realAssignedApplications.map((a) => a.application_id));
  productionClients.forEach((client) => {
    if (client.application_id) uniqueRealAssignedAppIds.add(client.application_id);
  });

  const realAssignedCount = uniqueRealAssignedAppIds.size;
  const totalClients = productionClients.length;
  const totalEmployees = productionClients.reduce(
    (sum, client) => sum + (employeeCounts[client.application_id] || 0),
    0
  );
  const totalEstimatedRevenue = productionClients.reduce((sum, client) => {
    const annualAdminCost = client.accepted_quote?.annual_admin_cost;
    const commissionRate = client.provider?.commission_percentage ?? 0.2;
    return sum + (annualAdminCost ? annualAdminCost * commissionRate : 0);
  }, 0);

  const revenuePerEmployee = totalEmployees > 0 ? totalEstimatedRevenue / totalEmployees : 0;
  const avgEmployeesPerClient = totalClients > 0 ? totalEmployees / totalClients : 0;
  const clientsPerMillion =
    revenuePerEmployee > 0 && avgEmployeesPerClient > 0
      ? 1_000_000 / revenuePerEmployee / avgEmployeesPerClient
      : 0;
  const winRate = realAssignedCount > 0 ? (totalClients / realAssignedCount) * 100 : 0;

  return {
    totalClients,
    totalEmployees,
    totalEstimatedRevenue,
    revenuePerEmployee,
    avgEmployeesPerClient,
    clientsPerMillion,
    realAssignedCount,
    winRate,
  };
}
